import { Prohibit } from '@phosphor-icons/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { users as usersApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/Bits';
import { useAsync } from '../../hooks/useAsync';
import { t } from '../../i18n';

/**
 * Les membres bloques.
 *
 * On peut debloquer d'ici, sans repasser par un profil qu'on ne veut
 * justement plus ouvrir. L'amitie, elle, ne revient pas : elle a ete
 * rompue au blocage et se redemande.
 */
export function BlockedScreen() {
  const { data, loading, error, reload } = useAsync(() => usersApi.blocked(), [], 'blocked');
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function unblock(userId: string) {
    setBusy(userId);
    setFailure(null);
    try {
      await usersApi.unblock(userId);
      reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.profil.bloques.impossible);
    } finally {
      setBusy(null);
    }
  }

  return (
    <SubScreen title={t.profil.commun.membresBloques}>
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}
      {failure && <p className="field__error">{failure}</p>}

      {data && data.length === 0 && (
        <EmptyState
          icon={<Prohibit size={30} />}
          title={t.profil.bloques.personne}
          hint={t.profil.bloques.personneAide}
        />
      )}

      {data && data.length > 0 && (
        <>
          <p className="pr-blocked__intro">
            {t.profil.bloques.explication}
          </p>

          {data.map((member) => (
            <div key={member.id} className="card ms-person">
              <Link to={`/app/members/${member.id}`} aria-label={member.username}>
                <Avatar user={member} size={48} />
              </Link>

              <div className="ms-person__body">
                <Link to={`/app/members/${member.id}`} className="ms-person__name">
                  {member.username}
                </Link>
                <p className="ms-person__note">{t.profil.bloques.bloque}</p>
              </div>

              <Button
                variant="soft"
                size="md"
                loading={busy === member.id}
                onClick={() => void unblock(member.id)}
              >
                {t.profil.commun.debloquer}
              </Button>
            </div>
          ))}
        </>
      )}
    </SubScreen>
  );
}
